import { canUpdateInPlace, updateItem, updateList } from "@/utils/storeHelper";
import { clearListCache, createKey, get } from "@/utils/networkCache";
import {
  SET_CHUNK,
  SET_ITEM,
  SET_LIST,
  SET_PAGINATION,
  SET_SORT,
  SET_STATUS,
  UPDATE_IN_PLACE,
} from "@/store/commonMutations";
import { request } from "leatherman-js";
import Status from "@/classes/Status";

const tickets = {
  namespaced: true,
  state: {
    chunk: null,
    item: null,
    list: [],
    pagination: null,
    sort: null,
    status: new Status(),
    ticketOptions: null,
  },
  mutations: {
    SET_CHUNK,
    SET_ITEM,
    SET_LIST,
    SET_PAGINATION,
    SET_SORT,
    SET_STATUS,
    UPDATE_IN_PLACE,
    SET_TICKET_OPTIONS(state, payload) {
      state.ticketOptions = payload;
    },
  },
  actions: {
    async get({ commit }, id) {
      const res = await get(createKey("tickets", { id }), request.makeParse, ["getTicket", { id }]);
      commit("SET_ITEM", res);
      return res;
    },
    async getList({ commit }, params) {
      commit("SET_STATUS", Status.LOADING);
      commit("SET_CHUNK", params);
      commit("SET_SORT", params);
      const res = await get(createKey("tickets-list", params), request.makeParse, [
        "getTickets",
        params,
      ]);
      commit("SET_LIST", res.list);
      commit("SET_PAGINATION", res.pagination);
      commit("SET_STATUS", Status.LOADED);
      return res;
    },
    async quickReply(context, { id, data }) {
      return request.makeParse("quickReplyTicket", { id }, data);
    },
    async thumbsUp(context, { id }) {
      const rating = await request.makeParse("ticketThumbsUp", { id });
      return rating;
    },
    async thumbsDown(context, { id }) {
      const rating = await request.makeParse("ticketThumbsDown", { id });
      return rating;
    },
    async closeTicket(context, { id }) {
      return request.makeParse("closeTicket", { id });
    },
    async updateTicket({ commit, dispatch, state }, payload) {
      if (state.item?.id === payload.id) {
        commit("SET_ITEM", payload);
      }
      if (canUpdateInPlace(state, payload)) {
        commit("UPDATE_IN_PLACE", payload);
      } else {
        dispatch("update", payload);
      }
    },
    async addTicketPost({ commit }, { id, comment, attachmentIds }) {
      const ticket = await request.makeParse(
        "addTicketComment",
        { id },
        { comment, attachmentIds }
      );
      const ticketPosts = await request.makeParse("getTicketPosts", {
        ticket_id: id,
        pageSize: 250,
      });

      ticket.posts = ticketPosts;
      commit("SET_ITEM", ticket);

      return ticket;
    },
    async ticketOptions({ commit }) {
      const ticketOptions = await request.makeParse(
        "getTicketOptions",
        {},
        {},
        { headers: { "Accept-Format": "nocworx/json-bundle" } }
      );
      commit("SET_TICKET_OPTIONS", ticketOptions);
      return ticketOptions;
    },
    async create({ dispatch, state }, data) {
      const ticket = await request.makeParse("addTicket", {}, data);
      clearListCache("tickets-list");
      updateList(dispatch, state);
      return ticket;
    },
    update({ commit, dispatch, state }, payload) {
      clearListCache("tickets-list");
      updateItem(commit, state, payload);
      updateList(dispatch, state);
    },
    refreshList({ dispatch, state }) {
      clearListCache("tickets-list");
      dispatch("getList", {
        pageIndex: state.chunk?.pagination?.index,
        pageSize: state.chunk?.pagination?.size,
        sortProperty: state.chunk?.sort?.property,
        sortDirection: state.chunk?.sort?.direction,
      });
    },
    async getTicket({ commit }, id) {
      const [ticket, ticketPosts, ticketPasswords] = await Promise.all([
        request.makeParse("getTicket", { id }),
        request.makeParse("getTicketPosts", { ticket_id: id, pageSize: 250 }),
        request.makeParse("getTicketPasswords", { id }),
      ]);

      ticket.posts = ticketPosts;
      ticket.passwords = ticketPasswords;

      commit("SET_ITEM", ticket);
      return ticket;
    },
    async createTicketPassword({ dispatch }, { id, password, name }) {
      await request.makeParse("createTicketPassword", null, {
        password,
        identity: name,
        ticketId: id,
      });

      return dispatch("getTicket", id);
    },
    async getTicketAttachment(context, { id, name }) {
      const verify = await request.makeParse("verifyAttachment", { id });
      const attachment = await request.makeDownload(
        "downloadAttachment",
        { id },
        null,
        null,
        "/",
        name
      );

      return { verify, attachment };
    },
    async addTicketRecipient({ dispatch }, { id, email }) {
      await request.makeParse("addTicketRecipient", { id }, email);

      return dispatch("getTicket", id);
    },
    async deleteTicketRecipient({ dispatch }, { id, recipientId }) {
      await request.makeParse("deleteTicketRecipient", { id }, recipientId);

      return dispatch("getTicket", id);
    },
    async deleteTicketPassword({ dispatch }, { id, passwordId }) {
      await request.makeParse("deleteTicketPassword", { id: passwordId });

      return dispatch("getTicket", id);
    },
  },
  getters: {
    getTicketOptions(state) {
      return state.ticketOptions;
    },
  },
};

export default tickets;
